import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { PropsWithChildren, ReactNode, useState } from "react";
import { FieldPath, FieldValues, UseFormTrigger } from "react-hook-form";
import { TFunction, useTranslation } from "react-i18next";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

export interface WizardStep<TFieldValues extends FieldValues = FieldValues> {
  index: number;
  title?: string;
  content: ReactNode;
  optional?: boolean;
  fieldsToValidate?: FieldPath<TFieldValues>[];
}

export interface WizardProps<TFieldValues extends FieldValues = FieldValues> {
  error?: Error | null;
  isLoading?: boolean;
  formId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  steps: WizardStep<TFieldValues>[];
  validationTrigger?: UseFormTrigger<TFieldValues>;
  onClose?: () => void;
  activeStepIndex: number;
  setActiveStepIndex: (step: number) => void;
}

const StepLabelAlert = ({ children }: PropsWithChildren) => {
  return (
    <Typography variant="caption" color="error">
      {children}
    </Typography>
  );
};

export interface StepContentProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  formId: string;
  className?: string;
  activeStep: WizardStep<TFieldValues>;
  steps: WizardStep<TFieldValues>[];
  setActiveStep: (step: number) => void;
  onSubmit?: () => void;
  onClose: () => void;
  isNextDisabled?: boolean;
  isBackDisabled?: boolean;
  onNext?: OnNextStepFunction;
}

type OnNextStepFunction = () =>
  | boolean
  | Promise<boolean | undefined>
  | undefined;

export interface NextStepButtonProps<
  TFieldValues extends FieldValues = FieldValues,
> extends ButtonProps {
  formId: string;
  onNext?: OnNextStepFunction;
  activeStep: WizardStep<TFieldValues>;
  setActiveStep: (step: number) => void;
  t: TFunction;
}

const NextStepButton = <TFieldValues extends FieldValues = FieldValues>({
  formId,
  onNext,
  activeStep,
  setActiveStep,
  t,
  ...props
}: NextStepButtonProps<TFieldValues>) => {
  const handleNext = async () => {
    const shouldContinue = await onNext?.();
    if (shouldContinue === false) {
      return;
    }
    setActiveStep(activeStep.index + 1);
  };

  return (
    <Button {...props} form={formId} color="inherit" onClick={handleNext}>
      {t("common:next")}
    </Button>
  );
};

export interface FinishWizardButtonProps extends ButtonProps {
  formId: string;
  onNext?: OnNextStepFunction;
  onSubmit?: () => void;
  t: TFunction;
}

const FinishWizardButton = ({
  formId,
  onNext,
  onSubmit,
  t,
  ...props
}: FinishWizardButtonProps) => {
  return (
    <Button
      {...props}
      type="submit"
      form={formId}
      color="inherit"
      onClick={onSubmit}
    >
      {t("common:finish")}
    </Button>
  );
};

const StepContent = <TFieldValues extends FieldValues = FieldValues>({
  formId,
  activeStep,
  steps,
  setActiveStep,
  onSubmit,
  onClose,
  onNext,
  isNextDisabled = false,
  isBackDisabled = false,
  ...props
}: StepContentProps<TFieldValues>) => {
  const { t } = useTranslation(["common"]);
  const isFirstStep = activeStep.index === 0;
  const isFinalStep = activeStep.index === steps.length - 1;

  const handleBack = () => {
    if (isFirstStep) {
      onClose();
      return;
    }
    setActiveStep(activeStep.index - 1);
  };

  const handleSkip = () => {
    setActiveStep(activeStep.index + 1);
  };

  return (
    <Box className={`${props.className} m-2`} {...props}>
      {activeStep.content}
      <Box className="flex flex-row pt-2">
        <Button color="inherit" onClick={handleBack}>
          {isFirstStep ? t("common:cancel") : t("common:back")}
        </Button>
        {activeStep.optional && (
          <Button color="inherit" onClick={handleSkip}>
            {t("common:skip")}
          </Button>
        )}
        <Box className="flex-auto" />
        {isFinalStep ? (
          <FinishWizardButton
            formId={formId}
            onSubmit={onSubmit}
            disabled={isNextDisabled}
            t={t}
          />
        ) : (
          <NextStepButton
            formId={formId}
            onNext={onNext}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            disabled={isNextDisabled}
            t={t}
          />
        )}
      </Box>
    </Box>
  );
};

const getStepTitle = <TFieldValues extends FieldValues = FieldValues>(
  step: WizardStep<TFieldValues>,
) => {
  return step.title ?? `Step ${step.index + 1}`;
};

const FormWizard = <TFieldValues extends FieldValues = FieldValues>({
  formId,
  isOpen,
  setIsOpen,
  steps,
  onClose,
  isBackDisabled = false,
  isNextDisabled = false,
  validationTrigger,
  activeStepIndex,
  setActiveStepIndex,
  error,
  isLoading = false,
}: WizardProps<TFieldValues>) => {
  const { t } = useTranslation(["common"]);
  const [errors, setErrors] = useState<Record<number, boolean>>({});

  const validateStep = async (): Promise<boolean | undefined> => {
    const activeStep = steps[activeStepIndex];
    const isValid = await validationTrigger?.(
      activeStep.fieldsToValidate ?? [],
    );

    setErrors((prev) => ({
      ...prev,
      [activeStepIndex]: !isValid,
    }));

    return isValid;
  };

  const resetWizard = () => {
    setActiveStepIndex(0);
    setErrors({});
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
    resetWizard();
  };

  const isStepError = (index: number) => errors[index] === true;

  const activeStep = steps[activeStepIndex];

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      {isLoading ? (
        <Box className="pt-4 w-full flex flex-col items-center p-2">
          <LoadingIndicator />
        </Box>
      ) : error ? (
        <Box className="p-4 flex flex-col gap-4">
          <Alert severity="error">{error.message}</Alert>
          <Box className="flex justify-end">
            <Button color="inherit" onClick={handleClose}>
              {t("common:close")}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className="pt-4 w-full flex flex-col justify-center p-2">
          <Box className="flex flex-row justify-center">
            <Stepper className="w-3/4" activeStep={activeStepIndex}>
              {steps.map((step) => (
                <Step key={getStepTitle(step)}>
                  <StepLabel
                    optional={<StepLabelAlert />}
                    error={isStepError(step.index)}
                  >
                    {getStepTitle(step)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          {activeStep && (
            <StepContent
              formId={formId}
              className="pt-4"
              activeStep={activeStep}
              setActiveStep={setActiveStepIndex}
              steps={steps}
              onNext={validateStep}
              onClose={handleClose}
              isNextDisabled={isNextDisabled ?? isStepError(activeStepIndex)}
              isBackDisabled={isBackDisabled}
            />
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default FormWizard;
